import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { assetIcon } from 'components/general/assets';
import { ArrayHelpers, StringHelpers, SystemText } from '@salesperformanceint/e4shared';
import config from '../../../../../../config';
import { setGoogleAuthToken } from 'actions/user';

export const GoogleDocs = ({ assets, allowMultiple, onAdd, mediaOnly }) => {
    const dispatch = useDispatch();

    const [files, setFiles] = React.useState(assets || []);
    const [error, setError] = React.useState('');
    const [pickerInit, setPickerInit] = React.useState(false);
    const [googleClient, setGoogleClient] = React.useState(null);
    const domain = useSelector(state => state.user.user.companySettings.googleDomain);
    const googleAuth = useSelector(state => state.user.googleAuthToken);

    React.useEffect(() => {
        const client = google.accounts.oauth2.initTokenClient({
            client_id: config.google.clientId,
            scope: config.google.scope.join(' '),
            callback: tokenResponse => {
                dispatch(setGoogleAuthToken(tokenResponse.access_token));
            },
        });

        setGoogleClient(client);
        gapi.load('picker', () => setPickerInit(true));
    }, []);

    const onPickerApiLoad = () => {
        let picker = new google.picker.PickerBuilder()
            .addView(google.picker.ViewId.DOCS)
            .setOAuthToken(googleAuth)
            .setDeveloperKey(config.google.key)
            .setCallback(pickerCallback)
            .build();
        picker.setVisible(true);
    };

    const pickerCallback = data => ArrayHelpers.existsAndHasLength(data?.docs) && addFile(data.docs[0]);

    const getToken = () => {
        googleClient?.requestAccessToken();
    };

    const revokeToken = () => {
        google.accounts.oauth2.revoke(googleAuth, () => {
            dispatch(setGoogleAuthToken(null));
        });
    };

    React.useEffect(() => {
        if (files) {
            onAdd(files, 'google');
        }
    }, [files]);

    const st = (path, ...rest) => SystemText(`General/Controls/FilePicker/GoogleDocs/${path}`, ...rest);

    const setPermissions = file => {
        fetch(`https://www.googleapis.com/drive/v3/files/${file.id}?key=${config.google.shareKey}`, {
            method: 'get',
            headers: new Headers({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + googleAuth }),
        }).then(response => response.json());

        fetch(`https://www.googleapis.com/drive/v3/files/${file.id}/permissions?key=${config.google.shareKey}`, {
            method: 'post',
            body: JSON.stringify({
                role: 'reader',
                type: domain ? 'domain' : 'anyone',
                domain: domain ? domain : undefined,
                allowFileDiscovery: true,
            }),
            headers: new Headers({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + googleAuth }),
        }).then(response => response.json());
    };

    const addFile = async file => {
        setError('');
        const exts = StringHelpers.arrayToString(config.mediaExtensions, ',', st('Or'));
        if (!files.some(f => f.internal_Id === file.id)) {
            if (mediaOnly && !file.type === 'video' && !StringHelpers.containsExt(file.name, config.mediaExtensions)) {
                setError(st('Error/Video', exts));
            } else {
                // Set share link.....
                await setPermissions(file);

                const safeFile = {
                    ...file,
                    size: file.sizeBytes,
                    internal_Id: file.id,
                    source: 'google',
                    ext: file.serviceId,
                };

                if (allowMultiple) {
                    setFiles(files.concat([safeFile]));
                } else {
                    setFiles([safeFile]);
                }
            }
        }
    };

    const removeFile = remove => {
        setError('');
        setFiles(files.filter(u => u.internal_Id !== remove.internal_Id));
    };

    return (
        <div>
            {googleAuth ? (
                <div>
                    <div className="text-danger">{st('Warning')}</div>
                    <button id="browse-google-button" className="btn btn-primary mt10" onClick={onPickerApiLoad}>
                        <i className="fa fa-google mr5" />
                        {st('Browse')}
                    </button>
                    {error && <div className="mt10 text-danger">{error}</div>}
                    {files.length > 0 && (
                        <div className="mt10">
                            <label>{st('Files')}</label>
                            {files
                                .sort((a, b) => (a.name > b.name ? 1 : -1))
                                .map((f, i) => {
                                    return (
                                        <div key={`file-${i}`} className="mb5 flex flex-center">
                                            <i className={`fa fa-${assetIcon(f)} mr5`} />
                                            <span className="ellipsis500 flex-grow">
                                                {f.name} {f.size > 0 && <span>- {StringHelpers.bytesToSize(f.size)}</span>}
                                            </span>
                                            <i
                                                className="ml5 text-danger fa fa-trash pointer text-right"
                                                title={`Remove the file ${f.name}`}
                                                onClick={() => removeFile(f)}
                                            />
                                        </div>
                                    );
                                })}
                        </div>
                    )}
                </div>
            ) : (
                <div className="text-center mt100">
                    <button id="login-google-button" className="btn btn-primary" onClick={getToken}>
                        <i className="fa fa-google mr5" />
                        {st('Login')}
                    </button>
                </div>
            )}
            {googleAuth && (
                <div className="mt20">
                    <button id="logout-google-button" className="btn btn-link btn-xs mt20" onClick={revokeToken}>
                        <span>{st('Logout')}</span>
                    </button>
                </div>
            )}
        </div>
    );
};

export default GoogleDocs;
